import { ArrowDownNarrowWide, ArrowUpNarrowWide, BookOpen, ExternalLink, FilePlus2, FileText, GitBranch, Loader2, Pencil, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { GraphLink, NoteCard, SourceNode } from "../types";
import { interfaceForKind } from "../lib/lifecycle";
import { MarkdownPreview } from "./MarkdownPreview";
import { NoteEditForm } from "./NoteEditForm";
import { StatusBadge } from "./StatusBadge";
import { StatusPicker } from "./StatusPicker";
import { TagPicker } from "./TagPicker";

type Props = {
  source?: SourceNode;
  sources: SourceNode[];
  notes: NoteCard[];
  graphLinks: GraphLink[];
  notesLoading?: boolean;
  notesError?: string;
  onOpenWorkspace: (source: SourceNode) => void;
  onCreateOutput: (input: {
    title: string;
    content: string;
    inputObjectIds: string[];
    parentSourceId?: string;
    tags?: string[];
    properties?: Record<string, unknown>;
  }) => Promise<string | undefined>;
  onUpdateOutput: (id: string, input: { title: string; content: string }) => Promise<void> | void;
  onUpdateNote: (id: string, input: { content: string }) => Promise<void> | void;
  onUpdateStatus: (id: string, status: string) => Promise<void> | void;
  onUpdateTags: (id: string, tags: string[]) => Promise<void> | void;
};

const noteTones: Record<string, string> = {
  critique: "border-rose-200 bg-rose-50/45",
  exercise: "border-amber-200 bg-amber-50/45",
  expansion: "border-fuchsia-200 bg-fuchsia-50/45",
  note: "border-slate-200 bg-white",
  practice: "border-emerald-200 bg-emerald-50/45",
  quote: "border-violet-200 bg-violet-50/45",
  reflection: "border-pink-200 bg-pink-50/45",
  task: "border-orange-200 bg-orange-50/45"
};

function noteTone(kind: string) {
  return noteTones[kind] ?? "border-slate-200 bg-white";
}

function noteMetaCount(note: NoteCard) {
  return note.relationCount + note.reviewCount + note.attachmentCount;
}

type OutputInput = {
  id: string;
  title: string;
  kind: string;
  content: string;
};

const noteLikeKinds = new Set(["note", "task", "quote", "exercise", "practice", "reflection", "critique", "expansion"]);

function isNoteLikeKind(kind: string) {
  return noteLikeKinds.has(kind);
}

function outputDraftFromInputs(sourceTitle: string, inputs: OutputInput[]) {
  const body = inputs
    .map((input) => {
      const content = input.content.trim();
      if (isNoteLikeKind(input.kind)) {
        return content || `- ${input.kind}`;
      }
      return [`## ${input.title}`, content || `- ${input.kind}`].join("\n\n");
    })
    .join("\n\n");

  return `# ${sourceTitle}\n\n${body}`;
}

export function SourceDetailPanel({
  source,
  sources,
  notes,
  graphLinks,
  notesLoading,
  notesError,
  onOpenWorkspace,
  onCreateOutput,
  onUpdateOutput,
  onUpdateNote,
  onUpdateStatus,
  onUpdateTags
}: Props) {
  const [newestFirst, setNewestFirst] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState("");
  const [selectedInputIds, setSelectedInputIds] = useState<string[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [outputTitle, setOutputTitle] = useState("");
  const [outputContent, setOutputContent] = useState("");
  const [isSavingOutput, setIsSavingOutput] = useState(false);
  const [outputError, setOutputError] = useState("");
  const [editingOutput, setEditingOutput] = useState(false);
  const [outputEditTitle, setOutputEditTitle] = useState("");
  const [outputEditContent, setOutputEditContent] = useState("");

  // All tags currently in use (sources + notes) — surfaced as suggestions in every TagPicker.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    notes.forEach((note) => (note.tags ?? []).forEach((tag) => set.add(tag)));
    sources.forEach((node) => (node.tags ?? []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [notes, sources]);

  // Custom (non-seeded) status slugs already in use, grouped by lifecycle interface, so a
  // status added on one node becomes a reusable option on every node sharing that interface.
  const customStatusesByInterface = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    const add = (kind: string, status?: string) => {
      if (!status) {
        return;
      }
      const key = interfaceForKind(kind);
      (map[key] ??= new Set()).add(status);
    };
    notes.forEach((note) => add(note.kind, note.status));
    sources.forEach((node) => add(node.kind, node.status));
    return map;
  }, [notes, sources]);

  const customFor = (kind: string) => Array.from(customStatusesByInterface[interfaceForKind(kind)] ?? []);

  const groups = useMemo(() => {
    if (!source) {
      return [];
    }
    const order = (list: NoteCard[]) => (newestFirst ? list : [...list].reverse());
    const notesFor = (id: string) => order(notes.filter((note) => note.sourceId === id));
    const children = sources.filter((node) => node.parentId === source.id);
    const result: Array<{ source: SourceNode; notes: NoteCard[] }> = [{ source, notes: notesFor(source.id) }];
    for (const child of children) {
      result.push({ source: child, notes: notesFor(child.id) });
    }
    return result;
  }, [source, sources, notes, newestFirst]);

  const noteInputs = useMemo(
    () =>
      groups.flatMap((group) =>
        group.notes.map((note) => ({
          id: note.id,
          title: note.title,
          kind: note.kind,
          content: note.content
        }))
      ),
    [groups]
  );

  const availableInputs = useMemo<OutputInput[]>(() => {
    if (!source || source.kind === "output") {
      return noteInputs;
    }

    return [
      {
        id: source.id,
        title: source.title,
        kind: source.kind,
        content: ""
      },
      ...noteInputs
    ];
  }, [noteInputs, source]);

  const selectedInputs = useMemo(
    () => selectedInputIds.map((id) => availableInputs.find((input) => input.id === id)).filter((input): input is OutputInput => Boolean(input)),
    [availableInputs, selectedInputIds]
  );

  const outputProvenance = useMemo(
    () => (source ? graphLinks.filter((link) => link.fromId === source.id && link.label === "produced_from") : []),
    [graphLinks, source]
  );

  useEffect(() => {
    setSelectedInputIds([]);
    setComposerOpen(false);
    setOutputTitle("");
    setOutputContent("");
    setOutputError("");
    setEditingOutput(false);
    setOutputEditTitle(source?.title ?? "");
    setOutputEditContent(source?.content ?? "");
  }, [source?.content, source?.id, source?.title]);

  const toggleInput = (id: string) => {
    setSelectedInputIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const openOutputComposer = () => {
    if (!source || selectedInputs.length === 0) {
      return;
    }

    setOutputTitle(`Bản nháp từ ${source.title}`);
    setOutputContent(outputDraftFromInputs(source.title, selectedInputs));
    setOutputError("");
    setComposerOpen(true);
  };

  const handleCreateOutput = async () => {
    if (!source || selectedInputs.length === 0) {
      return;
    }

    const title = outputTitle.trim();
    if (!title) {
      setOutputError("Cần có tiêu đề output.");
      return;
    }
    if (!outputContent.trim()) {
      setOutputError("Cần có nội dung output.");
      return;
    }

    setIsSavingOutput(true);
    setOutputError("");
    try {
      await onCreateOutput({
        title,
        content: outputContent,
        inputObjectIds: selectedInputs.map((input) => input.id),
        parentSourceId: source.kind === "output" ? undefined : source.id,
        tags: source.tags,
        properties: { createdFrom: "source_detail" }
      });
      setSelectedInputIds([]);
      setComposerOpen(false);
      setOutputTitle("");
      setOutputContent("");
    } catch (error) {
      setOutputError(error instanceof Error ? error.message : "Không tạo được output.");
    } finally {
      setIsSavingOutput(false);
    }
  };

  const handleUpdateExistingOutput = async () => {
    if (!source) {
      return;
    }
    const title = outputEditTitle.trim();
    if (!title) {
      setOutputError("Cần có tiêu đề output.");
      return;
    }
    if (!outputEditContent.trim()) {
      setOutputError("Cần có nội dung output.");
      return;
    }

    setIsSavingOutput(true);
    setOutputError("");
    try {
      await onUpdateOutput(source.id, { title, content: outputEditContent });
      setEditingOutput(false);
    } catch (error) {
      setOutputError(error instanceof Error ? error.message : "Không cập nhật được output.");
    } finally {
      setIsSavingOutput(false);
    }
  };

  if (!source) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-white px-6 py-16 text-center shadow-panel">
        <FileText size={26} className="mx-auto text-slate-300" aria-hidden="true" />
        <p className="mt-2 text-sm font-medium text-slate-600">Chọn một nguồn ở cột bên trái để xem note của nó.</p>
      </div>
    );
  }

  const isRead = source.kind === "article" || source.kind === "youtube" || source.kind === "output";
  const canCreateOutput = source.kind !== "output";
  const totalNotes = groups.reduce((sum, group) => sum + group.notes.length, 0);
  const visibleGroups = groups.filter((group) => group.notes.length > 0);

  return (
    <section className="rounded-lg border border-line bg-white shadow-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-bold">{source.title}</h1>
          <p className="mt-0.5 inline-flex flex-wrap items-center gap-2 text-sm text-muted">
            <StatusBadge kind={source.kind} />
            <span>{totalNotes} note</span>
            <span>·</span>
            <span>{groups.length - 1} mục con</span>
          </p>
          <div className="mt-1.5">
            <TagPicker tags={source.tags ?? []} availableTags={allTags} onChange={(tags) => onUpdateTags(source.id, tags)} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {source.kind === "output" ? (
            <button
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
              type="button"
              onClick={() => setEditingOutput((current) => !current)}
            >
              {editingOutput ? <X size={15} aria-hidden="true" /> : <Pencil size={15} aria-hidden="true" />}
              {editingOutput ? "Đóng edit" : "Edit output"}
            </button>
          ) : null}
          <button
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
            type="button"
            title={newestFirst ? "Đang xếp mới nhất trước" : "Đang xếp cũ nhất trước"}
            onClick={() => setNewestFirst((value) => !value)}
          >
            {newestFirst ? <ArrowDownNarrowWide size={15} aria-hidden="true" /> : <ArrowUpNarrowWide size={15} aria-hidden="true" />}
            {newestFirst ? "Mới nhất" : "Cũ nhất"}
          </button>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700"
            type="button"
            onClick={() => onOpenWorkspace(source)}
          >
            <BookOpen size={16} aria-hidden="true" />
            {isRead ? "Mở Link" : "Mở Book"}
          </button>
        </div>
      </div>

      <div className="p-4">
        {source.kind === "output" ? (
          <div className="mb-4 space-y-4">
            {editingOutput ? (
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                <div className="grid gap-2">
                  <input
                    className="h-9 rounded-lg border border-blue-200 bg-white px-3 text-sm font-bold text-ink outline-none focus:border-blue-400"
                    value={outputEditTitle}
                    onChange={(event) => setOutputEditTitle(event.target.value)}
                    placeholder="Tiêu đề output"
                    aria-label="Output title"
                  />
                  <textarea
                    className="min-h-48 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700 outline-none focus:border-blue-400"
                    value={outputEditContent}
                    onChange={(event) => setOutputEditContent(event.target.value)}
                    placeholder="Nội dung output"
                    aria-label="Output content"
                  />
                </div>
                {outputError ? <p className="mt-2 text-xs font-bold text-red-700">{outputError}</p> : null}
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                    type="button"
                    disabled={isSavingOutput}
                    onClick={() => setEditingOutput(false)}
                  >
                    <X size={14} aria-hidden="true" />
                    Hủy
                  </button>
                  <button
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    type="button"
                    disabled={isSavingOutput || !outputEditTitle.trim() || !outputEditContent.trim()}
                    onClick={() => void handleUpdateExistingOutput()}
                  >
                    {isSavingOutput ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
                    {isSavingOutput ? "Đang lưu" : "Lưu output"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-line bg-slate-50 p-4">
                <MarkdownPreview content={source.content ?? ""} emptyText="Output này chưa có nội dung." />
              </div>
            )}

            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-purple-700">
                <GitBranch size={14} aria-hidden="true" />
                Provenance
              </div>
              {outputProvenance.length === 0 ? (
                <p className="mt-3 text-sm font-semibold text-purple-700/80">Chưa có input `produced_from` cho output này.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {outputProvenance.map((link) => (
                    <article key={link.id ?? `${link.fromId}-${link.toId}`} className="rounded-lg border border-purple-100 bg-white px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge kind={link.toLabel ?? "note"} />
                        <h3 className="min-w-0 flex-1 truncate text-sm font-bold text-ink">{link.to}</h3>
                      </div>
                      {link.toContent ? <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">{link.toContent}</p> : null}
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {canCreateOutput ? (
          <div className="mb-4 rounded-lg border border-line bg-slate-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={selectedInputIds.includes(source.id)}
                  onChange={() => toggleInput(source.id)}
                  aria-label="Chọn nguồn hiện tại làm input output"
                />
                <span className="truncate">Nguồn hiện tại</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md border border-line bg-white px-2 py-1 text-xs font-bold text-muted">{selectedInputs.length} input</span>
                <button
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  type="button"
                  disabled={selectedInputs.length === 0}
                  onClick={openOutputComposer}
                >
                  <FilePlus2 size={15} aria-hidden="true" />
                  Tạo output
                </button>
              </div>
            </div>

            {composerOpen ? (
              <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                <div className="grid gap-2">
                  <input
                    className="h-9 rounded-lg border border-blue-200 bg-white px-3 text-sm font-bold text-ink outline-none focus:border-blue-400"
                    value={outputTitle}
                    onChange={(event) => setOutputTitle(event.target.value)}
                    placeholder="Tiêu đề output"
                    aria-label="Output title"
                  />
                  <textarea
                    className="min-h-48 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700 outline-none focus:border-blue-400"
                    value={outputContent}
                    onChange={(event) => setOutputContent(event.target.value)}
                    placeholder="Nội dung output"
                    aria-label="Output content"
                  />
                  <div className="rounded-lg border border-blue-100 bg-white p-3">
                    <MarkdownPreview content={outputContent} emptyText="Preview Markdown sẽ hiện ở đây." compact />
                  </div>
                </div>
                {outputError ? <p className="mt-2 text-xs font-bold text-red-700">{outputError}</p> : null}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-muted">{selectedInputs.map((input) => input.title).join(" · ")}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      type="button"
                      disabled={isSavingOutput}
                      onClick={() => setComposerOpen(false)}
                    >
                      <X size={14} aria-hidden="true" />
                      Hủy
                    </button>
                    <button
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      type="button"
                      disabled={isSavingOutput || !outputTitle.trim() || !outputContent.trim()}
                      onClick={() => void handleCreateOutput()}
                    >
                      {isSavingOutput ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
                      {isSavingOutput ? "Đang lưu" : "Lưu output"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {notesLoading && totalNotes === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-slate-50 px-4 py-8 text-center text-sm font-bold text-muted">
            Đang tải note của nguồn này...
          </div>
        ) : notesError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-8 text-center text-sm font-bold text-rose-700">
            {notesError}
          </div>
        ) : totalNotes === 0 && source.kind !== "output" ? (
          <div className="rounded-lg border border-dashed border-line bg-slate-50 px-4 py-8 text-center text-sm text-muted">
            Nguồn này và các mục con chưa có note. Sang <b>Ghi chép</b> để ghi note đầu tiên.
          </div>
        ) : (
          <div className="space-y-6">
            {visibleGroups.map((group) => {
              return (
                <div key={group.source.id} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h2 className="min-w-0 truncate text-base font-bold text-ink">{group.source.title}</h2>
                    <div className="h-px min-w-8 flex-1 bg-slate-200" />
                    {group.source.id !== source.id ? (
                      <TagPicker
                        tags={group.source.tags ?? []}
                        availableTags={allTags}
                        onChange={(tags) => onUpdateTags(group.source.id, tags)}
                        className="shrink-0"
                      />
                    ) : null}
                    <StatusPicker
                      kind={group.source.kind}
                      status={group.source.status}
                      customStatuses={customFor(group.source.kind)}
                      onChange={(status) => onUpdateStatus(group.source.id, status)}
                      className="shrink-0"
                    />
                    <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{group.notes.length} note</span>
                  </div>

                  <div className="space-y-3">
                    {group.notes.map((note) => {
                      const tone = noteTone(note.kind);
                      const metaCount = noteMetaCount(note);

                      return (
                        <article key={note.id} className={`rounded-lg border px-4 py-3 ${tone}`}>
                          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                            <div className="min-w-0">
                              {editingNoteId === note.id ? (
                                <NoteEditForm note={note} onSave={onUpdateNote} onCancel={() => setEditingNoteId("")} />
                              ) : (
                                <MarkdownPreview content={note.content} compact />
                              )}
                              {note.links && note.links.length > 0 ? (
                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                  {note.links.map((link) => (
                                    <a
                                      key={link.id}
                                      href={link.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={link.url}
                                      className="inline-flex h-7 max-w-[200px] items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-2 text-xs font-bold text-brand hover:bg-blue-100"
                                    >
                                      <ExternalLink size={12} aria-hidden="true" />
                                      <span className="truncate">{link.label}</span>
                                    </a>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                              {canCreateOutput ? (
                                <label className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={selectedInputIds.includes(note.id)}
                                    onChange={() => toggleInput(note.id)}
                                    aria-label={`Chọn ${note.title} làm input output`}
                                  />
                                  Input
                                </label>
                              ) : null}
                              <button
                                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                                type="button"
                                onClick={() => setEditingNoteId((current) => (current === note.id ? "" : note.id))}
                              >
                                <Pencil size={13} aria-hidden="true" />
                                Edit
                              </button>
                              <TagPicker
                                tags={note.tags ?? []}
                                availableTags={allTags}
                                onChange={(tags) => onUpdateTags(note.id, tags)}
                              />
                              <StatusPicker
                                kind={note.kind}
                                status={note.status}
                                customStatuses={customFor(note.kind)}
                                onChange={(status) => onUpdateStatus(note.id, status)}
                              />
                              <StatusBadge kind={note.kind} />
                              {metaCount > 0 ? (
                                <span className="inline-flex h-6 items-center rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-600">
                                  {metaCount}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
